import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { doc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useToast } from '@/components/ui/use-toast';
import { Eye, EyeOff, Shield, Check, AlertCircle, User, Mail, Lock, Loader2 } from 'lucide-react';
import { EmailAPI } from '@/services/emailAPI';

// Simple debounce implementation
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const signupSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  username: z.string()
    .min(3, 'Username must be at least 3 characters')
    .max(20, 'Username must be less than 20 characters')
    .regex(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores')
    .refine(username => !username.startsWith('_') && !username.endsWith('_'), 'Username cannot start or end with underscore'),
  email: z.string().email('Invalid email address'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/, 'Password must contain uppercase, lowercase, number and special character'),
  confirmPassword: z.string(),
  acceptTerms: z.boolean().refine(val => val === true, 'You must accept the terms and conditions'),
  enableTwoFactor: z.boolean().optional(),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type SignupFormData = z.infer<typeof signupSchema>;

interface PasswordStrength {
  score: number;
  feedback: string[];
  color: string;
}

interface SignupFormProps {
  onSuccess: () => void;
  onSwitchToLogin: () => void;
}

export function SignupForm({ onSuccess, onSwitchToLogin }: SignupFormProps) {
  const { register, handleSubmit, formState: { errors, isSubmitting }, watch, setError, clearErrors, setValue } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    mode: 'onChange',
  });
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({ score: 0, feedback: [], color: 'bg-gray-200' });
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameSuggestions, setUsernameSuggestions] = useState<string[]>([]);

  const password = watch('password');
  const username = watch('username');
  const confirmPassword = watch('confirmPassword');

  const passwordChecks = {
    length: (password || '').length >= 8,
    lowercase: /[a-z]/.test(password || ''),
    uppercase: /[A-Z]/.test(password || ''),
    number: /\d/.test(password || ''),
    special: /[@$!%*?&]/.test(password || ''),
  };

  // Password strength checker
  const checkPasswordStrength = useCallback((password: string): PasswordStrength => {
    if (!password) return { score: 0, feedback: [], color: 'bg-gray-200' };

    let score = 0;
    const feedback: string[] = [];

    if (password.length >= 8) score += 1;
    else feedback.push('Use at least 8 characters');

    if (/[a-z]/.test(password)) score += 1;
    else feedback.push('Include lowercase letters');

    if (/[A-Z]/.test(password)) score += 1;
    else feedback.push('Include uppercase letters');

    if (/\d/.test(password)) score += 1;
    else feedback.push('Include numbers');

    if (/[@$!%*?&]/.test(password)) score += 1;
    else feedback.push('Include special characters');

    const colors = ['bg-red-500', 'bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-green-500'];
    return {
      score,
      feedback,
      color: colors[score] || 'bg-gray-200'
    };
  }, []);

  // Username availability checker
  const checkUsernameAvailability = useCallback(
    debounce(async (username: string) => {
      if (!username || username.length < 3) {
        setUsernameAvailable(null);
        setUsernameSuggestions([]);
        return;
      }

      setCheckingUsername(true);
      try {
        const normalizedUsername = username.trim().toLowerCase();
        const q = query(collection(db, 'users'), where('usernameLower', '==', normalizedUsername));
        const querySnapshot = await getDocs(q);
        const available = querySnapshot.empty;
        setUsernameAvailable(available);
        
        if (!available) {
          setError('username', { message: 'Username is already taken' });
          setUsernameSuggestions([
            `${normalizedUsername}${Math.floor(Math.random() * 90 + 10)}`,
            `${normalizedUsername}_official`,
            `${normalizedUsername}_${new Date().getFullYear()}`,
          ]);
        } else {
          clearErrors('username');
          setUsernameSuggestions([]);
        }
      } catch (error) {
        console.error('Error checking username:', error);
        setUsernameAvailable(null);
        setUsernameSuggestions([]);
      } finally {
        setCheckingUsername(false);
      }
    }, 500),
    [setError, clearErrors]
  );

  // Watch for password changes
  React.useEffect(() => {
    setPasswordStrength(checkPasswordStrength(password || ''));
  }, [password, checkPasswordStrength]);

  // Watch for username changes
  React.useEffect(() => {
    if (username) {
      checkUsernameAvailability(username);
    }
  }, [username, checkUsernameAvailability]);

  const onSubmit = async (data: SignupFormData) => {
    const normalizedName = data.name.trim().replace(/\s+/g, ' ');
    const normalizedUsername = data.username.trim();
    const normalizedEmail = data.email.trim().toLowerCase();

    try {
      // Final username availability check
      const q = query(collection(db, 'users'), where('usernameLower', '==', normalizedUsername.toLowerCase()));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        setError('username', { message: 'Username is already taken' });
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, data.password);
      const user = userCredential.user;

      // Setup profile data without deleting a successfully created auth account.
      // Non-critical Firestore setup errors should not force sign-out.
      await updateProfile(user, {
        displayName: normalizedName,
      });

      const nowIso = new Date().toISOString();

      await setDoc(doc(db, 'users', user.uid), {
        id: user.uid,
        name: normalizedName,
        nameLower: normalizedName.toLowerCase(),
        email: normalizedEmail,
        emailLower: normalizedEmail,
        username: normalizedUsername,
        usernameLower: normalizedUsername.toLowerCase(),
        photoURL: null,
        coverURL: null,
        bio: '',
        location: '',
        website: '',
        isOnline: true,
        lastSeen: nowIso,
        createdAt: nowIso,
        updatedAt: nowIso,
        isAdmin: false,
        isSeller: false,
        role: 'user',
        permissions: [],
        isVerified: false,
        twoFactorEnabled: data.enableTwoFactor || false,
        profileComplete: false,
        onboarding: {
          profile: false,
          firstPost: false,
          firstConnection: false,
        },
        preferences: {
          theme: 'system',
          language: 'en',
          notifications: {
            email: true,
            push: true,
            sms: false
          }
        }
      });

      try {
        await setDoc(doc(db, 'privacy_settings', user.uid), {
          userId: user.uid,
          profileVisibility: 'public',
          showEmail: false,
          showPhone: false,
          showBirthday: true,
          showLocation: true,
          showOnlineStatus: true,
          showLastSeen: true,
          whoCanSeeMyPosts: 'public',
          whoCanCommentOnMyPosts: 'everyone',
          whoCanShareMyPosts: 'everyone',
          whoCanTagMeInPosts: 'friends',
          whoCanTagMeInPhotos: 'friends',
          whoCanMessageMe: 'everyone',
          whoCanCallMe: 'friends',
          readReceipts: true,
          typingIndicators: true,
          allowSearchByEmail: true,
          allowSearchByPhone: false,
          showInSearchResults: true,
          showInSuggestions: true,
          activityStatus: true,
          dataCollection: true,
          personalizedAds: true,
          shareUsageData: false,
          twoFactorEnabled: data.enableTwoFactor || false,
          loginNotifications: true,
          unusualActivityAlerts: true,
          blockedUsers: [],
          createdAt: nowIso,
          updatedAt: nowIso
        });
      } catch (privacySetupError) {
        console.warn('Privacy settings initialization failed, continuing signup:', privacySetupError);
      }

      await sendEmailVerification(user);
      await EmailAPI.sendWelcomeEmail(normalizedEmail, normalizedName);

      toast({
        title: 'Account Created Successfully! 🎉',
        description: `Welcome to InTouch, @${normalizedUsername}! We also sent a verification email to ${normalizedEmail}.`,
      });

      onSuccess();
    } catch (error: any) {
      console.error('Signup error:', error);
      let errorMessage = 'Failed to create account';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      }
      
      toast({
        title: 'Registration Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-3">
          <User className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold">Create Your Account</h3>
        <p className="text-gray-600">Join our community and connect with others</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Full Name */}
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium flex items-center gap-2">
            <User className="w-4 h-4" />
            Full Name
          </Label>
          <Input
            id="name"
            placeholder="Enter your full name"
            {...register('name')}
            error={errors.name?.message}
            className="h-11"
          />
        </div>

        {/* Username */}
        <div className="space-y-2">
          <Label htmlFor="username" className="text-sm font-medium flex items-center gap-2">
            <User className="w-4 h-4" />
            Username
          </Label>
          <div className="relative">
            <Input
              id="username"
              placeholder="Choose a unique username"
              {...register('username')}
              error={errors.username?.message}
              className="h-11 pr-10"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {checkingUsername && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
              {!checkingUsername && usernameAvailable === true && <Check className="w-4 h-4 text-green-500" />}
              {!checkingUsername && usernameAvailable === false && <AlertCircle className="w-4 h-4 text-red-500" />}
            </div>
          </div>
          {usernameAvailable === true && (
            <p className="text-xs text-green-600 flex items-center gap-1">
              <Check className="w-3 h-3" />
              Username is available
            </p>
          )}
          {usernameAvailable === false && usernameSuggestions.length > 0 && (
            <div className="text-xs text-gray-600">
              <p className="mb-1">Try one of these:</p>
              <div className="flex flex-wrap gap-1.5">
                {usernameSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => {
                      setValue('username', suggestion, { shouldValidate: true, shouldDirty: true });
                      clearErrors('username');
                    }}
                    className="px-2 py-1 rounded-full border border-blue-200 text-blue-700 hover:bg-blue-50"
                  >
                    @{suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email Address
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email address"
            {...register('email')}
            error={errors.email?.message}
            className="h-11"
          />
        </div>

        {/* Password */}
        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Password
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              {...register('password')}
              error={errors.password?.message}
              className="h-11 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          
          {/* Password Strength Indicator */}
          {password && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                    style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500">
                  {passwordStrength.score === 0 && 'Very Weak'}
                  {passwordStrength.score === 1 && 'Weak'}
                  {passwordStrength.score === 2 && 'Fair'}
                  {passwordStrength.score === 3 && 'Good'}
                  {passwordStrength.score === 4 && 'Strong'}
                  {passwordStrength.score === 5 && 'Very Strong'}
                </span>
              </div>
              {passwordStrength.feedback.length > 0 && (
                <div className="text-xs text-gray-500">
                  <p>Suggestions:</p>
                  <ul className="list-disc list-inside ml-2">
                    {passwordStrength.feedback.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-2 gap-1 text-xs">
                <p className={passwordChecks.length ? 'text-green-600' : 'text-gray-500'}>8+ characters</p>
                <p className={passwordChecks.lowercase ? 'text-green-600' : 'text-gray-500'}>lowercase letter</p>
                <p className={passwordChecks.uppercase ? 'text-green-600' : 'text-gray-500'}>uppercase letter</p>
                <p className={passwordChecks.number ? 'text-green-600' : 'text-gray-500'}>number</p>
                <p className={passwordChecks.special ? 'text-green-600' : 'text-gray-500'}>special character</p>
              </div>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-sm font-medium flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Confirm Password
          </Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              {...register('confirmPassword')}
              error={errors.confirmPassword?.message}
              className="h-11 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {confirmPassword && password && (
            <p className={`text-xs ${confirmPassword === password ? 'text-green-600' : 'text-red-600'}`}>
              {confirmPassword === password ? 'Passwords match' : 'Passwords do not match'}
            </p>
          )}
        </div>

        {/* Two-Factor Authentication Option */}
        <div className="flex items-center space-x-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <Checkbox
            id="enableTwoFactor"
            {...register('enableTwoFactor')}
          />
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-600" />
            <Label htmlFor="enableTwoFactor" className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Enable two-factor authentication (recommended)
            </Label>
          </div>
        </div>

        {/* Terms and Conditions */}
        <div className="flex items-start space-x-2">
          <Checkbox
            id="acceptTerms"
            {...register('acceptTerms')}
            className="mt-1"
          />
          <Label htmlFor="acceptTerms" className="text-sm text-gray-600 leading-relaxed">
            I agree to the{' '}
            <a href="/terms" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </Label>
        </div>
        {errors.acceptTerms && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.acceptTerms.message}
          </p>
        )}

        <Button 
          type="submit" 
          className="w-full h-11 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-lg shadow-lg transition-all duration-200 transform hover:scale-[1.02]" 
          disabled={isSubmitting || checkingUsername || usernameAvailable === false}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating your account...
            </>
          ) : (
            'Create Account'
          )}
        </Button>
      </form>

      <div className="text-center text-sm text-gray-500">
        Already have an account?{' '}
        <button 
          type="button"
          onClick={onSwitchToLogin}
          className="text-blue-600 hover:underline font-medium"
        >
          Sign in instead
        </button>
      </div>
    </div>
  );
}